import { Button, Textarea, TextField } from '@ads/components-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NOTIFICATION_VARIABLES } from '../../../hooks/useNotificationMessage';

const TITLE_MAX = 60;
const BODY_MAX = 180;

export interface NotificationMessageFormProps {
  readonly title: string;
  readonly body: string;
  readonly url: string;
  readonly studentCount: number;
  readonly onTitleChange: (value: string) => void;
  readonly onBodyChange: (value: string) => void;
  readonly onUrlChange: (value: string) => void;
}

/**
 * Molecule de composição da mensagem de notificação.
 *
 * Exibe campos de título, descrição e URL com contadores de caracteres.
 * Botões de variável inserem o texto na posição do cursor.
 * A variável {{nome}} é ocultada quando o número de alunos selecionados superar 1000.
 *
 * @param studentCount - Total de alunos selecionados na etapa de público
 */
export function NotificationMessageForm({
  title,
  body,
  url,
  studentCount,
  onTitleChange,
  onBodyChange,
  onUrlChange,
}: NotificationMessageFormProps) {
  const { t } = useTranslation('notification');
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [lastFocused, setLastFocused] = useState<'title' | 'body'>('body');
  const [_titleCursor, setTitleCursor] = useState(0);
  const [bodyCursor, setBodyCursor] = useState(0);

  const trackTitleCursor = (e: React.SyntheticEvent<HTMLElement>) =>
    setTitleCursor((e.target as HTMLInputElement).selectionStart ?? title.length);

  const trackBodyCursor = () => setBodyCursor(bodyRef.current?.selectionStart ?? body.length);

  const insertVariable = (variable: string) => {
    if (lastFocused === 'title') {
      return;
    }

    const next = (body.slice(0, bodyCursor) + variable + body.slice(bodyCursor)).slice(0, BODY_MAX);
    onBodyChange(next);
    const newPos = Math.min(bodyCursor + variable.length, BODY_MAX);
    requestAnimationFrame(() => {
      if (bodyRef.current) {
        bodyRef.current.selectionStart = newPos;
        bodyRef.current.selectionEnd = newPos;
        bodyRef.current.focus();
        setBodyCursor(newPos);
      }
    });
  };

  const visibleVariables = NOTIFICATION_VARIABLES.filter(
    v => !(v.key === 'nome' && studentCount > 1000),
  );

  return (
    <div className="flex flex-1 flex-col gap-mini">
      {/* Campo: título */}
      <TextField
        id="notif-title"
        label={t('wizard.message.form.titleLabel')}
        placeholder={t('wizard.message.form.titlePlaceholder')}
        value={title}
        onChange={e => onTitleChange(e.target.value)}
        onFocus={() => setLastFocused('title')}
        onClick={trackTitleCursor}
        onKeyUp={trackTitleCursor}
        size="md"
        maxLength={TITLE_MAX}
        fullWidth
      />

      {/* Campo: descrição */}
      <Textarea
        ref={bodyRef}
        id="notif-body"
        label={t('wizard.message.form.bodyLabel')}
        placeholder={t('wizard.message.form.bodyPlaceholder')}
        value={body}
        rows={7}
        resizable
        fullWidth
        maxLength={BODY_MAX}
        onChange={e => onBodyChange(e.target.value)}
        onFocus={() => setLastFocused('body')}
        onClick={trackBodyCursor}
        onKeyUp={trackBodyCursor}
      />

      {/* Variáveis disponíveis */}
      <div>
        <p className="ts-heading-xxs !font-medium text-ctx-content-title">
          {t('wizard.message.form.variablesLabel')}
        </p>
        <p className="ts-paragraph-xxs text-ctx-content-base">
          {t('wizard.message.form.variablesDescription')}
        </p>
      </div>
      <div className="flex flex-wrap gap-xxs">
        {visibleVariables.map(v => (
          <Button
            key={v.key}
            hierarchy="secondary"
            size="sm"
            onClick={() => insertVariable(v.label)}
          >
            {v.label}
          </Button>
        ))}
      </div>

      {/* Campo: URL de destino */}
      <TextField
        id="notif-url"
        label={t('wizard.message.form.urlLabel')}
        placeholder={t('wizard.message.form.urlPlaceholder')}
        value={url}
        onChange={e => onUrlChange(e.target.value)}
        size="md"
        fullWidth
      />
    </div>
  );
}
